import React, { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import "./App.css"; // Styles for App layout and global elements
import Home from "./pages/Home";
import Students from "./pages/Students";
import StudentProfile from "./components/StudentProfile"; // Updated path to components directory
import { StudentDashboard } from "./components/StudentDashboard"; // Import StudentDashboard component
import GroupProfile from "./components/GroupProfile"; // Import GroupProfile component
import Lessons from "./pages/Lessons";
import LessonWorkspace from './pages/LessonWorkspace';
import EmbeddedLessonView from './components/EmbeddedLessonView';
import Schedule from './pages/Schedule';
import Business from './pages/Business';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import BlogAdmin from './pages/BlogAdmin';
import BlogPostEditor from './pages/BlogPostEditor';
import NovasCommunity from './pages/NovasAI';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import Register from './components/Register'; // Import Register component
import Login from './components/Login'; // Import Login component
import ProtectedRoute from './components/ProtectedRoute'; // Import ProtectedRoute component
import AdminRoute, { isBlogAdmin } from './components/AdminRoute'; // Blog admin gate — not every teacher, only the site owner
import DataMigration from './components/DataMigration'; // Import DataMigration component
import ProfileSettings from './components/ProfileSettings'; // Import ProfileSettings component
import EmailConfirmation from './components/EmailConfirmation'; // Import EmailConfirmation component
import EmailChangeConfirmation from './components/EmailChangeConfirmation';
import DictionaryTest from './components/DictionaryTest'; // Import DictionaryTest component
import { ActiveEditorContext } from "./context/ActiveEditorContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Landing from "./pages/Landing";


// Data for sidebar links
const sidebarLinks = [
  { name: "Home", emoji: "🏠", path: "/" },
  { name: "Students", emoji: "🧑‍🎓", path: "/students" },
  { name: "Lessons", emoji: "📚", path: "/lessons" },
  { name: "Create Lesson", emoji: "💚", path: "/lessons/new" },
  { name: "Dictionary Test", emoji: "🔬", path: "/dictionary-test" },
  {
    name: "Schedule",
    path: "/schedule",
    icon: (
      <svg
        width="24"
        height="24"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M5 5a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1 2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2ZM3 19v-7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Zm6.01-6a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-10 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    name: "Business",
    path: "/business",
    icon: (
      <svg
        width="24"
        height="24"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 2a3 3 0 0 0-3 3v1H5a3 3 0 0 0-3 3v2.382l1.447.723.005.003.027.013.12.056c.108.05.272.123.486.212.429.177 1.056.416 1.834.655C7.481 13.524 9.63 14 12 14c2.372 0 4.52-.475 6.08-.956.78-.24 1.406-.478 1.835-.655a14.028 14.028 0 0 0 .606-.268l.027-.013.005-.002L22 11.381V9a3 3 0 0 0-3-3h-2V5a3 3 0 0 0-3-3h-4Zm5 4V5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v1h6Zm6.447 7.894.553-.276V19a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v-5.382l.553.276.002.002.004.002.013.006.041.02.151.07c.13.06.318.144.557.242.478.198 1.163.46 2.01.72C7.019 15.476 9.37 16 12 16c2.628 0 4.98-.525 6.67-1.044a22.95 22.95 0 0 0 2.01-.72 15.994 15.994 0 0 0 .707-.312l.041-.02.013-.006.004-.002.001-.001-.431-.866.432.865ZM12 10a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2H12Z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    name: "Blog",
    emoji: "📝",
    path: "/blog/admin",
    adminOnly: true,
  },
  { name: "Novas Community", emoji: "🌐", path: "/community" },
  { name: "Apps", emoji: "📱", path: "/apps" },
];

function App() {
  const [activeEditor, setActiveEditor] = useState(null);
  // Hide the Blog sidebar entry for every teacher account except the
  // configured admin(s) — showing a link that just bounces you back to "/"
  // on click would be confusing.
  const visibleSidebarLinks = sidebarLinks.filter((link) => !link.adminOnly || isBlogAdmin());

  // ── Auth callback rescue ────────────────────────────────────────────────
  // When Supabase confirms an email it redirects back to the project's
  // Site URL with auth tokens stuffed into the URL fragment
  // (#access_token=...&type=signup&...). Some Supabase configurations
  // strip our explicit emailRedirectTo down to just the Site URL, which
  // means the user lands on "/" instead of "/auth/callback" and our
  // EmailConfirmation never runs (no teacher/student row gets linked).
  // We rescue that by detecting the signup fragment on ANY route and
  // bouncing the user to /auth/callback ourselves, fragment intact.
  useEffect(() => {
    try {
      const hash = window.location.hash || '';
      const hasAccessToken = hash.includes('access_token=');
      const isSignupCallback = hasAccessToken && (
        hash.includes('type=signup') || hash.includes('type=recovery') || hash.includes('type=magiclink')
      );
      const isEmailChangeCallback = hasAccessToken && hash.includes('type=email_change');
      if (isSignupCallback && window.location.pathname !== '/auth/callback') {
        const newUrl = `${window.location.origin}/auth/callback${window.location.search}${hash}`;
        window.location.replace(newUrl);
      } else if (isEmailChangeCallback && window.location.pathname !== '/auth/email-changed') {
        const newUrl = `${window.location.origin}/auth/email-changed${window.location.search}${hash}`;
        window.location.replace(newUrl);
      }
    } catch (_) {}
  }, []);

  return (
    <ErrorBoundary>
    <ActiveEditorContext.Provider value={{ activeEditor, setActiveEditor }}>
      <Routes>
        {/* Non-authenticated pages - without sidebar */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/migrate" element={<DataMigration />} />

        {/* Public blog — no auth required */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        
        {/* Root: marketing landing for visitors; teacher home (gated) for
            authenticated users. ProtectedRoute then handles the student
            redirect path. */}
        <Route path="/" element={
          (localStorage.getItem('isAuthenticated') === 'true')
            ? (
              <ProtectedRoute>
                <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
                  <Home />
                </AuthenticatedLayout>
              </ProtectedRoute>
            )
            : <Landing />
        } />
        <Route path="/students" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <Students />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/students/new" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <StudentProfile isNew={true} />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/students/:studentId" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <StudentProfile />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/groups/new" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <GroupProfile isNew={true} />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/groups/:groupId" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <GroupProfile />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/lessons" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <Lessons />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/lessons/new" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <LessonWorkspace />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/lessons/:lessonId" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <LessonWorkspace />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/lessons/:lessonId/embed" element={
          <ProtectedRoute>
            <EmbeddedLessonView />
          </ProtectedRoute>
        } />
        <Route path="/schedule" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <Schedule />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/business" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <Business />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/blog/admin" element={
          <AdminRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <BlogAdmin />
            </AuthenticatedLayout>
          </AdminRoute>
        } />
        <Route path="/blog/admin/new" element={
          <AdminRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <BlogPostEditor />
            </AuthenticatedLayout>
          </AdminRoute>
        } />
        <Route path="/blog/admin/:id/edit" element={
          <AdminRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <BlogPostEditor />
            </AuthenticatedLayout>
          </AdminRoute>
        } />
        <Route path="/community" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <NovasCommunity />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/profile-settings" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <ProfileSettings />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/dictionary-test" element={
          <ProtectedRoute>
            <AuthenticatedLayout sidebarLinks={visibleSidebarLinks}>
              <DictionaryTest />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />

        {/* Student Invite - handles invite links and redirects to registration */}
        <Route path="/student-invite/:studentSlug" element={<Register />} />
        <Route path="/register/:studentSlug" element={<Register />} />
        
        {/* Email Confirmation - handles email verification callback */}
        <Route path="/auth/callback" element={<EmailConfirmation />} />
        <Route path="/auth/email-changed" element={<EmailChangeConfirmation />} />
        
        {/* Student Dashboard - requires authentication */}
        <Route path="/student-dashboard/:studentSlug" element={<StudentDashboard />} />
        
        {/* Student Lesson View - для просмотра урока студентом */}
        <Route path="/student-dashboard/:studentSlug/lesson/:lessonId" element={<StudentDashboard />} />

      </Routes>
    </ActiveEditorContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
 